import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/axios';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';

const InventoryPage = () => {
  const [activeTab, setActiveTab] = useState('input'); // 'input', 'wastage', 'history'
  const [products, setProducts] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updates, setUpdates] = useState({}); // { productId: quantity }
  const [notes, setNotes] = useState({}); // { productId: notes }
  const [historyDate, setHistoryDate] = useState(new Date().toISOString().split('T')[0]);
  const toast = useToast();
  const { user } = useAuth();

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/products?limit=200'); 
      setProducts(data.data.products);
      setUpdates({});
      setNotes({});
    } catch {
      toast('Failed to load products', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/wastage/history?date=${historyDate}`);
      setHistory(data.data.logs);
    } catch {
      toast('Failed to load wastage history', 'error');
    } finally {
      setLoading(false);
    }
  }, [historyDate, toast]);

  useEffect(() => {
    if (activeTab === 'input' || activeTab === 'wastage') {
      fetchProducts();
    } else if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab, fetchProducts, fetchHistory]);

  const handleInputChange = (productId, value) => {
    const val = parseInt(value);
    setUpdates(prev => ({
      ...prev,
      [productId]: isNaN(val) ? '' : val
    }));
  };

  const handleNoteChange = (productId, value) => {
    setNotes(prev => ({
      ...prev,
      [productId]: value
    }));
  };

  const handleSaveInput = async () => {
    const payload = Object.entries(updates)
      .filter(([_, qty]) => typeof qty === 'number' && qty > 0)
      .map(([id, qty]) => ({ id, quantityToAdd: qty }));

    if (payload.length === 0) {
      toast('No stock updates to save.', 'info');
      return;
    }

    setSaving(true);
    try {
      await api.patch('/products/bulk-stock', { updates: payload });
      toast('Daily input saved successfully!', 'success');
      fetchProducts();
    } catch {
      toast('Failed to save inventory updates', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWastage = async () => {
    const checkStock = Object.entries(updates).find(([id, qty]) => {
      if (typeof qty !== 'number' || qty <= 0) return false;
      const prod = products.find(p => p.id === id);
      return prod && qty > prod.stock_quantity;
    });

    if (checkStock) {
      toast('Wastage quantity cannot exceed current stock', 'error');
      return;
    }

    const payload = Object.entries(updates)
      .filter(([_, qty]) => typeof qty === 'number' && qty > 0)
      .map(([id, qty]) => ({ id, quantityToWaste: qty, notes: notes[id] || '' }));

    if (payload.length === 0) {
      toast('No wastage updates to save.', 'info');
      return;
    }

    setSaving(true);
    try {
      await api.post('/wastage', { updates: payload, loggedBy: user?.fullName || user?.username || 'Admin' });
      toast('Wastage recorded successfully!', 'success');
      fetchProducts();
    } catch {
      toast('Failed to record wastage', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700 }}>📦 Inventory Management</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
            Manage daily incoming stock and record end-of-day wastage.
          </p>
        </div>
        
        {/* Tabs */}
        <div style={{ display: 'flex', background: 'var(--bg-card)', padding: 4, borderRadius: 12, border: '1px solid var(--border)' }}>
          <button 
            className={`btn btn-sm ${activeTab === 'input' ? 'btn-primary' : 'btn-ghost'}`} 
            onClick={() => setActiveTab('input')}
            style={{ borderRadius: 8, padding: '8px 16px', fontWeight: 600, border: 'none', background: activeTab === 'input' ? 'var(--primary)' : 'transparent', color: activeTab === 'input' ? '#fff' : 'var(--text-muted)' }}
          >
            📥 Daily Input
          </button>
          <button 
            className={`btn btn-sm ${activeTab === 'wastage' ? 'btn-primary' : 'btn-ghost'}`} 
            onClick={() => setActiveTab('wastage')}
            style={{ borderRadius: 8, padding: '8px 16px', fontWeight: 600, border: 'none', background: activeTab === 'wastage' ? '#f43f5e' : 'transparent', color: activeTab === 'wastage' ? '#fff' : 'var(--text-muted)' }}
          >
            🗑️ Record Wastage
          </button>
          <button 
            className={`btn btn-sm ${activeTab === 'history' ? 'btn-primary' : 'btn-ghost'}`} 
            onClick={() => setActiveTab('history')}
            style={{ borderRadius: 8, padding: '8px 16px', fontWeight: 600, border: 'none', background: activeTab === 'history' ? '#8b5cf6' : 'transparent', color: activeTab === 'history' ? '#fff' : 'var(--text-muted)' }}
          >
            📜 Wastage History
          </button>
        </div>
      </div>

      <div className="card">
        {/* Helper Action Bar */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {activeTab === 'input' && (
            <>
              <div style={{ fontWeight: 600, color: 'var(--primary)' }}>Enter new stock arriving today (+ Add)</div>
              <button className="btn btn-primary" onClick={handleSaveInput} disabled={saving || Object.values(updates).every(v => !v)}>
                {saving ? 'Saving...' : '💾 Save Incoming Stock'}
              </button>
            </>
          )}
          {activeTab === 'wastage' && (
            <>
              <div style={{ fontWeight: 600, color: '#f43f5e' }}>Record end-of-day damaged/spoiled items (- Deduct)</div>
              <button className="btn btn-danger" onClick={handleSaveWastage} disabled={saving || Object.values(updates).every(v => !v)} style={{ background: '#f43f5e', border: 'none' }}>
                {saving ? 'Saving...' : '🗑️ Save Wastage'}
              </button>
            </>
          )}
          {activeTab === 'history' && (
            <>
              <div style={{ fontWeight: 600, color: '#8b5cf6' }}>Review past wastage records</div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Date:</span>
                <input type="date" className="form-input" value={historyDate} onChange={e => setHistoryDate(e.target.value)} style={{ padding: '6px 12px' }} />
                <button className="btn btn-secondary btn-sm" onClick={() => setHistoryDate('')}>All</button>
              </div>
            </>
          )}
        </div>

        {loading ? (
          <div className="flex-center" style={{ height: 200 }}><div className="spinner"></div></div>
        ) : (activeTab === 'input' || activeTab === 'wastage') && products.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">🥒</div><p>No products available</p></div>
        ) : activeTab === 'history' && history.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">📜</div><p>No wastage records found for selected date.</p></div>
        ) : activeTab === 'history' ? (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Date & Time</th>
                  <th>Product</th>
                  <th>Quantity Lost</th>
                  <th>Logged By</th>
                  <th>Notes / Reason</th>
                </tr>
              </thead>
              <tbody>
                {history.map(log => (
                  <tr key={log.id}>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{new Date(log.created_at).toLocaleDateString()}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{new Date(log.created_at).toLocaleTimeString()}</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {log.image_url ? <img src={log.image_url} alt="" style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover' }} /> : <div style={{ width: 32, height: 32, borderRadius: 6, background: 'var(--dark-3)' }} />}
                        <span style={{ fontWeight: 600 }}>{log.product_name}</span>
                      </div>
                    </td>
                    <td style={{ color: '#f43f5e', fontWeight: 700 }}>-{log.quantity} units</td>
                    <td style={{ fontSize: 13 }}>{log.logged_by || '—'}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13, maxWidth: 200 }}>{log.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th style={{ textAlign: 'center' }}>Current Stock</th>
                  <th style={{ width: activeTab === 'wastage' ? 120 : 180 }}>{activeTab === 'input' ? '+ Daily Input (Add)' : '- Waste Qty'}</th>
                  {activeTab === 'wastage' && <th style={{ width: 250 }}>Reason / Notes</th>}
                </tr>
              </thead>
              <tbody>
                {products.map(p => {
                  const outOfStock = p.stock_quantity <= 0;
                  return (
                    <tr key={p.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          {p.image_url ? (
                            <img src={p.image_url} alt={p.name} style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--dark-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🛒</div>
                          )}
                          <div style={{ fontWeight: 600 }}>{p.name}</div>
                        </div>
                      </td>
                      <td style={{ fontSize: 13, textTransform: 'capitalize' }}>{p.category}</td>
                      <td style={{ fontWeight: 600 }}>₹{p.price}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${outOfStock ? 'badge-cancelled' : 'badge-delivered'}`} style={{ fontSize: 14, padding: '6px 16px' }}>
                          {p.stock_quantity}
                        </span>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="0"
                          min="0"
                          max={activeTab === 'wastage' ? p.stock_quantity : undefined}
                          value={updates[p.id] || ''}
                          onChange={e => handleInputChange(p.id, e.target.value)}
                          style={{ 
                            background: 'var(--dark-3)', border: '1px solid var(--border)', borderRadius: 6, 
                            padding: '8px 12px', color: 'var(--text)', width: '100%' 
                          }}
                        />
                      </td>
                      {activeTab === 'wastage' && (
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g. Expired, Damaged"
                            value={notes[p.id] || ''}
                            onChange={e => handleNoteChange(p.id, e.target.value)}
                            style={{ 
                              background: 'var(--dark-3)', border: '1px solid var(--border)', borderRadius: 6, 
                              padding: '8px 12px', color: 'var(--text)', width: '100%' 
                            }}
                          />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryPage;
